import { FC, useContext } from "react";
import { useDebouncedCurrentGame } from "../../../../hooks/useDebouncedCurrentGame";
import {
  Anchor,
  Badge,
  Box,
  Flex,
  Paper,
  Skeleton,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { TeamName } from "../../../../Components/TeamName";
import { CompetitionContext } from "../../../../CompetitionContext.tsx";
import { TeamLogo } from "../../../../Components/TeamLogo.tsx";
import { Link } from "react-router-dom";
import { IconLivePhoto } from "@tabler/icons-react";
import classes from "./CurrentGame.module.css";

export const CurrentGame: FC = () => {
  const competitionId = useContext(CompetitionContext);
  const currentGame = useDebouncedCurrentGame(competitionId, 5000);
  const theme = useMantineTheme();

  if (!currentGame.isSuccess) {
    return null;
  }

  const now = new Date();
  const gameStartTime = new Date(currentGame.data.dateTime);

  // Hide if game is more than 5 minutes away and not started
  if (
    gameStartTime.getTime() - now.getTime() > 5 * 60 * 1000 &&
    !currentGame.data.started
  ) {
    return null;
  }

  const { homeTeamId, guestTeamId } = currentGame.data.actualTeams;

  if (!homeTeamId || !guestTeamId) {
    return <Skeleton height={64} w="100%" radius={0} mx="-md" />;
  }

  const isLive = currentGame.data.started && !currentGame.data.result;

  return (
    <Paper
      radius={0}
      p={0}
      mx="-md"
      mb="md"
      style={{
        background: `linear-gradient(135deg, ${theme.colors.fwcBlue[9]} 0%, ${theme.colors.fwcBlue[7]} 100%)`,
      }}
    >
      <Flex align="center" justify="space-between" px="md" py="sm" gap="sm">
        {/* Home Team */}
        <Anchor
          component={Link}
          to={`/teams/${homeTeamId}`}
          underline="never"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Flex align="center" gap="xs">
            <Flex
              w={40}
              h={40}
              align="center"
              justify="center"
              style={{
                background: "rgba(255,255,255,0.15)",
                borderRadius: "50%",
                flexShrink: 0,
              }}
            >
              <Box w={26}>
                <TeamLogo teamId={homeTeamId} height={26} />
              </Box>
            </Flex>
            <Text
              c="white"
              fw={600}
              size="sm"
              truncate
              style={{ lineHeight: 1.2 }}
            >
              <TeamName teamId={homeTeamId} variant="medium" />
            </Text>
          </Flex>
        </Anchor>

        {/* Score & Live Badge */}
        <Flex
          direction="column"
          align="center"
          gap={4}
          style={{ flexShrink: 0 }}
        >
          <Text
            c="white"
            fw={700}
            style={{
              fontSize: 28,
              fontFamily: "Chivo Mono",
              lineHeight: 1,
            }}
          >
            {currentGame.data.score.home}:{currentGame.data.score.guest}
          </Text>
          {isLive && (
            <Badge
              color="red"
              variant="filled"
              size="xs"
              leftSection={<IconLivePhoto size={10} />}
              className={classes.liveBadge}
            >
              LIVE
            </Badge>
          )}
          {!isLive && currentGame.data.started && (
            <Text c="white" opacity={0.6} size="xs">
              Beendet
            </Text>
          )}
        </Flex>

        {/* Guest Team */}
        <Anchor
          component={Link}
          to={`/teams/${guestTeamId}`}
          underline="never"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Flex align="center" gap="xs" justify="flex-end">
            <Text
              c="white"
              fw={600}
              size="sm"
              truncate
              ta="right"
              style={{ lineHeight: 1.2 }}
            >
              <TeamName teamId={guestTeamId} variant="medium" />
            </Text>
            <Flex
              w={40}
              h={40}
              align="center"
              justify="center"
              style={{
                background: "rgba(255,255,255,0.15)",
                borderRadius: "50%",
                flexShrink: 0,
              }}
            >
              <Box w={26}>
                <TeamLogo teamId={guestTeamId} height={26} />
              </Box>
            </Flex>
          </Flex>
        </Anchor>
      </Flex>
    </Paper>
  );
};
